import type {
  Collection as Discoveries,
  Discovery,
  Discovery$Audio
} from '#~/device/discovery/index.js';
import type { Device$Audio, Collection as Devices, Minimist } from '#~/device/index.js';
import type { ChildrenOnly } from '@studio/design-system/props';
import type { Agent } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import type { Device } from '@studio/models/device';
import type { Token } from '@studio/models/livekit';
import type { Room } from '@studio/models/room';
import type * as S from '@studio/react-utils/store';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import type { UseBoundStoreWithEqualityFn } from 'zustand/traditional';

/** Mutable data draft. */
export type Draft = S.Draft<State>;

/** Preview device. */
export interface Preview {
  /** Device ID or `false` on error. */
  deviceId: Device['id'];

  /** Error flag. */
  error?: T_Maybe<Error>;

  /** URX. */
  urx: string;
}

/** Immer producer. */
export type Producer = S.Producer<State>;

/** `<Provider>` properties. */
export type Props = ChildrenOnly & {
  agentIds: Agent['id'][];
  appId: AppId;
  devices: Devices;
  discoveries: Discoveries;
  roomId: Room['id'];
};

/** Store state. */
export type State = Readonly<{
  /** Application ID. */
  appId: AppId;

  /** Candidate device. */
  candidate: Omit<Minimist, 'agentId' | 'id'>;

  /** Flow ID for tracking multi-step workflow */
  flowId: string;

  /** Selected panel. */
  panel: Panel;

  /** Preview devices. */
  previews: T_Optional<{
    audio: Preview;

    /** Associated video device (if applicable). */
    video: {
      /** Device ID. */
      deviceId: Device['id'];
    };
  }>;

  /** Immer producer. */
  produce: Producer;

  /** Room ID. */
  roomId: Room['id'];

  /** Settings panel state. */
  settings: {
    /** Ready flag. */
    isReady: boolean;

    /** Saving flag. */
    isSaving: boolean;
  };

  /** Source panel state. */
  source: {
    /** Discovered audio devices. */
    discoveries: Discovery$Audio[];

    /** Existing audio devices. */
    existing: Device$Audio[];

    /** Ready flag. */
    isReady: boolean;

    /** Preferred discovery ID. */
    preferredId?: T_Maybe<Discovery['id']>;

    /** Selected discovery. */
    selection: T_Maybe<Discovery>;
  };

  /** LiveKit token. */
  token?: T_Maybe<Token>;
}> & {
  /** Agent ID. */
  agentId: Agent['id'];
};

/** Workflow store. */
export type Store = UseBoundStoreWithEqualityFn<S.Store<State>>;

/** Workflow panels. */
export enum Panel {
  Source = 1,
  Settings
}
